import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Controller, Control, FieldValues } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';

import { ControllerOnChangeType } from 'types/input-types';

import 'react-phone-input-2/lib/style.css';
// type ColorType = 'primary' | 'secondary' | 'error' | 'info' | 'success';
type InputType = 'text' | 'email' | 'number' | 'password' | 'tel';
type MultilineProps =
  | {
      multiline?: true;
      rows: number;
    }
  | {
      multiline?: false;
      rows?: never;
    };

type ControlProps =
  | {
      control?: unknown;
      name: string;
    }
  | {
      control?: false | undefined;
      name?: never;
    };

type InputProps = {
  className?: string;
  helperText?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: InputType;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  disableHelperText?: boolean;
  defaultValue?: string;
  id?: string;
} & MultilineProps &
  ControlProps;

export function TextInput({
  type = 'text',
  className,
  helperText,
  error,
  placeholder,
  value,
  onChange,
  disabled,
  fullWidth,
  multiline,
  rows = 6,
  control,
  name,
  disableHelperText,
  defaultValue,
  id,
}: InputProps) {
  const [showPassword, setShowPassword] = useState<string>();
  useEffect(() => {
    if (type) {
      setShowPassword(type);
    }
  }, [type]);

  const classes = useMemo(
    () =>
      classNames(
        'px-2 py-3 rounded-md shadow-sm focus:outline-none placeholder:text-zinc-400 placeholder:text-md text-md bg-background-light w-full text-background-contrastText',
        className,
        {
          'opacity-60 cursor-not-allowed': disabled,
        },
        {
          'border border-error-main': !!error,
        }
      ),

    [className, disabled, error]
  );

  const getInput = useCallback(
    (
      controlChange?: ControllerOnChangeType,
      formValue?: FieldValues['value']
    ) =>
      multiline ? (
        <textarea
          id={id}
          className={classes}
          placeholder={placeholder}
          value={control ? formValue : value}
          onChange={
            control
              ? controlChange
              : (e) => {
                  onChange?.(e.target.value);
                }
          }
          disabled={disabled}
          rows={rows}
          defaultValue={defaultValue}
        />
      ) : name === 'phone' ? (
        <PhoneInput
          inputProps={{
            name,
            className: classNames(classes, 'pl-12 !z-5'),
            id,
            disabled,
          }}
          value={control ? formValue || defaultValue : value || defaultValue}
          onChange={
            control
              ? (phone: string) =>
                  controlChange?.({ target: { value: phone, name } })
              : (phone: string) => {
                  onChange?.(phone);
                }
          }
          placeholder={placeholder}
          disabled={disabled}
          containerClass="!rounded-2xl !bg-background-light "
          searchClass="[&>input]:!border-none !py-2 !pl-2 !bg-background-light !text-background-contrastText !outline-none"
          dropdownClass="[&>li]:!border-b [&>li]:!border-zinc-500 [&>li[role]]:!border-none [&>li[role]]:!p-3 [&>li[role]]:!px-4 [&>li[role]]:!text-background-contrastText [&>li[role]:hover]:!bg-primary-main [&>li[role]:hover]:!text-primary-contrastText [&>li[aria-selected]]:!bg-primary-main !bg-background-light !text-background-contrastText !ml-0 !mt-0 !shadow-none"
          buttonClass={classNames(
            "!rounded-md !z-0 overflow-hidden [&>div[role='button']]:!bg-background-light !bg-background-light  !outline-none !m-1 !text-background-contrastText !border-none"
          )}
          searchPlaceholder="Search"
          disableDropdown
          country="us"
          disableCountryGuess
          countryCodeEditable={false}
        />
      ) : (
        <div className="relative">
          <input
            id={id}
            name={name ?? ''}
            type={showPassword ?? type}
            className={classes}
            placeholder={placeholder}
            value={control ? formValue : value}
            onChange={
              control
                ? controlChange
                : (e) => {
                    onChange?.(e.target.value);
                  }
            }
            disabled={disabled}
            defaultValue={defaultValue}
          />
          {type === 'password' &&
            (showPassword === 'password' ? (
              <EyeSlashIcon
                onClick={() => setShowPassword('text')}
                className="absolute right-4 top-3 h-[20px] cursor-pointer text-zinc-400"
              />
            ) : (
              <EyeIcon
                onClick={() => setShowPassword('password')}
                className="absolute right-4 top-3 h-[20px] cursor-pointer text-zinc-400"
              />
            ))}
        </div>
      ),
    [
      classes,
      disabled,
      multiline,
      onChange,
      placeholder,
      rows,
      type,
      value,
      control,
      defaultValue,
      id,
      name,
      showPassword,
    ]
  );

  return (
    <div className={fullWidth ? 'w-full' : 'w-60'}>
      {control ? (
        <Controller
          name={name as string}
          control={control as Control}
          render={({
            field: { onChange: controlChange, value: fieldValues },
          }) => getInput(controlChange, fieldValues)}
        />
      ) : (
        getInput()
      )}
      {!disableHelperText && (
        <p
          style={{ minHeight: '0.8rem' }}
          className={classNames(
            'm-1 text-left text-sm text-background-contrastText ',
            {
              '!text-error-main': !!error,
            }
          )}>
          {helperText}
        </p>
      )}
    </div>
  );
}
